import { PhoneTiltProvider } from './PhoneTilt';
import { createContext, useCallback, useContext, useLayoutEffect, useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { createThemeTransition } from './theme-transition';
import './theme-transition.css';
import { activateTheme, APPEARANCE_KEY, DEFAULT_APPEARANCE, normalizeAppearance, readAppearance, THEMES, themeVariables } from './appearance';

const AppearanceContext = createContext(null);
export function AppearanceProvider({ children }) {
  const [preferences, setPreferences] = useState(() => { const initial = readAppearance(); activateTheme(initial.theme); return initial; });
  const [storageAvailable, setStorageAvailable] = useState(true);
  const requested = useRef(preferences), current = useRef(preferences), transition = useRef(null);
  const theme = THEMES[preferences.theme];
  const update = useCallback((patch, {animate = true} = {}) => {
    const next = normalizeAppearance({ ...requested.current, ...patch });
    requested.current = next;
    transition.current ||= createThemeTransition();
    transition.current.run(sync => {
      const apply = () => { activateTheme(next.theme); setPreferences(next); };
      if (sync) flushSync(apply); else apply();
    }, {changed:next.theme !== current.current.theme,from:THEMES[current.current.theme].bg,quiet:next.quiet,animate});
  }, []);
  const selectTheme = useCallback((id,options) => { if (Object.prototype.hasOwnProperty.call(THEMES,id)) update({theme:id},options); }, [update]);
  useLayoutEffect(() => {
    current.current = preferences;
    activateTheme(preferences.theme);
    for (const node of [document.documentElement, document.body]) {
      for (const [key, value] of Object.entries(themeVariables(theme))) node.style.setProperty(key, value);
      node.dataset.theme = preferences.theme;
      node.dataset.layout = preferences.layout;
      node.dataset.density = preferences.density;
      node.dataset.orbitQuiet = String(preferences.quiet);
    }
    document.body.classList.toggle('light-mode', theme.light);
    document.documentElement.style.colorScheme = theme.light ? 'light' : 'dark';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content',theme.bg);
    try {
      localStorage.setItem(APPEARANCE_KEY, JSON.stringify(preferences));
      localStorage.setItem('tradelog-theme', theme.light ? 'light' : 'dark');
      setStorageAvailable(true);
    } catch { setStorageAvailable(false); }
  }, [preferences, theme]);
  useEffect(() => () => { transition.current?.stop(); transition.current = null; }, []);
  useEffect(() => {
    const visibility = () => { document.documentElement.dataset.motionHidden = String(document.hidden); };
    visibility(); document.addEventListener('visibilitychange', visibility);
    return () => document.removeEventListener('visibilitychange', visibility);
  }, []);
  useEffect(() => {
    const sync = event => {
      if (event.key !== APPEARANCE_KEY && event.key !== null) return;
      update(readAppearance());
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, [update]);
  return <AppearanceContext.Provider value={{ preferences, theme, isDark:!theme.light, update, selectTheme, storageAvailable }}><PhoneTiltProvider quiet={preferences.quiet} enabled={preferences.phoneTilt} onEnabledChange={phoneTilt=>update({phoneTilt})}>{children}</PhoneTiltProvider></AppearanceContext.Provider>;
}
export function useAppearance() {
  return useContext(AppearanceContext) || {preferences:DEFAULT_APPEARANCE,theme:THEMES.classic,isDark:true,update:()=>{},selectTheme:()=>{},storageAvailable:true};
}
